import isEmpty from 'lodash/isEmpty';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from 'shared/components/form/button';
import { notify } from 'shared/components/notification/notification';
import { API_CONFIG } from 'shared/constants/constants';
import { useQuery } from 'shared/hooks/useQuery';
import { PlusWhiteIcon, WhiteDownloadIcon } from 'shared/icons/icon';
import httpService from 'shared/services/http.service';
import i18n from 'shared/util/localization';
import localizationConstants from 'shared/util/translation/constants';
import { Translate } from 'shared/util/translation/translate';

import { has } from 'lodash';
import {
	IChartData,
	IChartDetails,
	IChartSelection,
	IChartSettingsType,
	ICompareChartIndicators,
	ISearchMaterialData
} from '../interface/futureTable';
import { useQueryParams } from 'shared/hooks/useQueryParams';
import AddPricePopup from './addPricePopup';
import ExportDataPopup from './exportDataPopup';
import SetupPriceAlert from './setUpPriceAlert';

interface IProps {
	chartDetails: IChartDetails;
	chartData: IChartData[];
	chartSelection: IChartSelection;
	customChartSpan: {
		von: string;
		bis: string;
	};
	comparativeChartIndicator: ICompareChartIndicators[] | ISearchMaterialData[] | IChartSettingsType[];
	strokeColors: any;
	id: string;
	action: string;
	setAction: (action: string) => void;
	symbol: string;
	chartSettings: IChartSettingsType[];
	scaleType: string;
	compareValue: string;
	forecastKeys: string[];
	chartFrequency: string[];
	updateAllChartDetails: { [key: string]: IChartDetails };
	isExportDisabled: boolean;
}

const ExportMaterialDataButtons: React.FC<IProps> = (props) => {
	const params = useQuery();
	const { elementId, timeSpan, currency } = useQueryParams();
	const navigate = useNavigate();
	const { t } = i18n;
	const [loading, setLoading] = useState(false);
	const {
		chartDetails,
		chartData,
		chartSelection,
		comparativeChartIndicator,
		customChartSpan,
		strokeColors,
		chartSettings,
		scaleType,
		action,
		id,
		setAction,
		symbol,
		compareValue,
		forecastKeys,
		chartFrequency,
		updateAllChartDetails,
		isExportDisabled
	} = props;
	const location = useLocation();
	const addChartToDashboardFunction = () => {
		const { boerse, symbol, basis_symbol } = chartDetails;
		const updatedCoparativeIndicator = comparativeChartIndicator.map((ele) => {
			if (!has(ele, 'isNormalize')) {
				return {
					...ele,
					isNormalize: true
				};
			} else {
				return ele;
			}
		});
		if (!isEmpty(chartDetails)) {
			const advanceChartConfig: any = { chartSettings, scaleType };
			const payload: any = {
				boerse,
				symbol,
				basis_symbol,
				element_id: params.get('element_id'),
				chartConfig: {
					chartSelection: { ...chartSelection, forecastKeys },
					customChartSpan: customChartSpan,
					comparativeChartIndicator: updatedCoparativeIndicator,
					strokeColors: strokeColors,
					advanceChartConfig
				},
				name: compareValue
			};
			if (chartSettings.length < 2) {
				delete payload.name;
			}
			setLoading(true);
			httpService
				.post(API_CONFIG.path.addChartToDashboard, payload)
				.then((res) => {
					res.message === 'success' &&
						notify(t(elementId ? localizationConstants.updatedToDashboard : localizationConstants.addedToDashboard), 'success', {
							id: 'addChart'
						});

					const isChartScreen = location.pathname.includes('chartScreen');
					const baseState = { ...location.state, name: compareValue, chartSelection: { ...chartSelection, forecastKeys } };

					let url = isChartScreen
						? `/materials/chartScreen`
						: `materials/futureTable?boerse=${encodeURIComponent(boerse || '')}&basis_symbol=${encodeURIComponent(
								basis_symbol || ''
						  )}&materialParentId=0&symbol=${encodeURIComponent(symbol || '')}&currency=${currency}`;
					if (elementId) {
						url = isChartScreen
							? `${url}?timeSpan=${timeSpan}&currency=${currency}&element_id=${elementId}`
							: `${url}&element_id=${elementId}`;
					}

					navigate(url, { state: baseState });
					setLoading(false);
				})
				.catch((err) => {
					setLoading(false);
					console.error(err);
				});
		}
	};

	return (
		<>
			<div className='d-flex'>
				<Button
					btnType='primary'
					className='mr-8 padding-left-right--16px'
					icon={<PlusWhiteIcon className='mr-10 icon-small ' />}
					iconPosition='left'
					onClick={() => setAction('setAlert')}
					disabled={chartDetails.disallowed === 1 || location.pathname.includes('chartScreen') || isEmpty(chartDetails.fields)}
				>
					<Translate text={localizationConstants.setUpPriceAlert} />
				</Button>
				<Button
					btnType='primary'
					className='mr-8 padding-left-right--16px'
					icon={<PlusWhiteIcon className='mr-10 icon-small ' />}
					iconPosition='left'
					onClick={() => setAction('addPrice')}
					disabled={location.pathname.includes('chartScreen')}
				>
					<Translate text={localizationConstants.addToPrice} />
				</Button>
				<Button
					btnType='primary'
					className='mr-8 padding-left-right--16px'
					icon={<PlusWhiteIcon className='mr-10 icon-small ' />}
					iconPosition='left'
					onClick={addChartToDashboardFunction}
					loading={loading}
					disabled={loading}
				>
					<Translate text={elementId ? localizationConstants.updateChartToDashboard : localizationConstants.addChartToDashboard} />
				</Button>
				<div className='export-btn-wrapper'>
					<Button
						btnType='primary'
						iconPosition='left'
						icon={<WhiteDownloadIcon className='mr-10' />}
						className=' padding-left-right--16px font-weight--500 line-height--20px'
						onClick={() => {
							setAction('downloadCourse');
						}}
						disabled={
							isEmpty(chartData) ||
							chartData.length < 7 ||
							(!isEmpty(updateAllChartDetails) &&
								isEmpty(Object.values(updateAllChartDetails).filter((item) => item.disallowed === 0))) ||
							isExportDisabled
						}
					>
						<Translate text={localizationConstants.exportData} />
					</Button>
				</div>
			</div>
			{action === 'setAlert' && (
				<SetupPriceAlert chartDetails={chartDetails} setAction={setAction} boerse={params.get('boerse')} symbol={params.get('symbol')} />
			)}
			{action === 'addPrice' && (
				<AddPricePopup
					setAction={setAction}
					boerse={params.get('boerse')}
					symbol={params.get('symbol')}
					chartSelection={chartSelection}
					chartSettings={chartSettings}
					comparativeChartIndicator={comparativeChartIndicator}
					customChartSpan={customChartSpan}
					strokeColors={strokeColors}
					scaleType={scaleType}
				/>
			)}
			{action === 'downloadCourse' && (
				<ExportDataPopup
					setAction={setAction}
					chartDetails={chartDetails}
					span={chartSelection.span}
					customChartSpan={customChartSpan}
					chartType={chartSelection.type}
					id={id}
					symbol={symbol}
					comparativeChartIndicator={comparativeChartIndicator}
					chartSettings={chartSettings}
					scaleType={scaleType}
					chartData={chartData}
					chartPeriod={chartSelection.span}
					forecastKeys={forecastKeys}
					chartFrequency={chartFrequency}
					selectedDataTypesPerMaterial={chartSelection.selectedDataTypesPerMaterial}
					selectedFrequencies={chartSelection.selectedFrequencies}
					allChartDetails={updateAllChartDetails}
					currencyType={chartSelection.currencyType as string}
				/>
			)}
		</>
	);
};

export default ExportMaterialDataButtons;
